import { Logger } from '@frontend/logger';
import { TransactionClient, TransactionExtendedClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import type { Dispatch } from 'react';
import { useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    trackingNumber: string;
    setTrackingNumber: Dispatch<string | null>;
    isSubmitting: boolean;
    isGeneratingTrackingNumber: boolean;
    existingTransactions: Transaction[] | null;
    continuingTransactionId: string | null;
    currentUserId: string | null;
    submitDisabled: boolean;
    onSubmit: () => Promise<void>;
    onGenerateTrackingNumber: () => Promise<void>;
    onContinueWithExistingTransaction: Dispatch<Transaction>;
    onBack: () => void;
}

const findExistingTransactions = async (accountId: string, trackingNumber: string, currentTransactionId?: string): Promise<Transaction[]> => {
    const response = await TransactionClient.fetchTransactions({
        account_id: accountId,
        tracking_number: trackingNumber
    });

    return response.results.filter((transaction) => {
        if (transaction.tracking_number !== trackingNumber) return false;
        if (currentTransactionId && transaction.id === currentTransactionId) return false;
        return true;
    });
};

const canContinueWithExistingDraftTransaction = (transaction: Transaction, currentUserId: string | null | undefined): boolean => {
    return transaction.state === TransactionStateName.TRANSACTION_DRAFT && !!currentUserId && transaction.user_id === currentUserId;
};

const resolveGeneratedTrackingNumber = (response: unknown): string | null => {
    if (!response) return null;
    if (typeof response === 'string') return response.trim() || null;
    if (typeof response !== 'object') return null;

    const value = response as Record<string, unknown>;
    const candidates = [value.unique_identifier, value.uniqueIdentifier, value.tracking_number, value.trackingNumber, value.id];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim();
    }

    return null;
};

const useEnterTrackingNumber = (): ViewProps => {
    const { navigationState, store, wizardId, updateWizardData, onBack, onSubmit: navigateToNextStep } = useScreen();
    const { syncTransaction, localUpdateTransaction } = useWizardTransaction({ wizardId, store });
    const currentUserId = navigationState.user?.id ?? null;
    const [trackingNumber, setTrackingNumberState] = useState<string>(store?.data.trackingNumber || store?.data.transaction?.tracking_number || '');
    const [existingTransactions, setExistingTransactions] = useState<Transaction[] | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [continuingTransactionId, setContinuingTransactionId] = useState<string | null>(null);
    const [isGeneratingTrackingNumber, setIsGeneratingTrackingNumber] = useState<boolean>(false);

    const setTrackingNumber = (value: string | null) => {
        setTrackingNumberState(value ?? '');
        setExistingTransactions(null);
    };

    const runTrackingNumberConflictCheck = async (normalizedTrackingNumber: string, currentTransactionId?: string) => {
        if (!store) return [];

        const matches = await findExistingTransactions(store.wizard.account_id, normalizedTrackingNumber, currentTransactionId ?? store.data.transaction?.id);
        setExistingTransactions(matches.length > 0 ? matches : null);
        return matches;
    };

    const onGenerateTrackingNumber = async () => {
        if (!store || isGeneratingTrackingNumber || isSubmitting) return;

        setIsGeneratingTrackingNumber(true);
        try {
            const response = await TransactionClient.fetchTransactionUniqueIdentifier(store.wizard.account_id);
            const generatedTrackingNumber = resolveGeneratedTrackingNumber(response);
            if (generatedTrackingNumber) {
                setTrackingNumberState(generatedTrackingNumber);
                setExistingTransactions(null);
            }
        } catch (error) {
            Logger.error('Failed to generate tracking number', error);
        } finally {
            setIsGeneratingTrackingNumber(false);
        }
    };

    const proceedToNextStep = (normalizedTrackingNumber: string, transaction: Transaction) => {
        if (!store) return;

        setExistingTransactions(null);
        updateWizardData({
            trackingNumber: normalizedTrackingNumber,
            transaction
        });

        const nextStep = store.steps.find((step) => step.screen === ScreenType.ENTER_TRACKING_NUMBER && step.trigger === ScreenTrigger.SUBMIT);
        navigateToNextStep(nextStep);
    };

    const onContinueWithExistingTransaction = async (transaction: Transaction) => {
        if (!store || isSubmitting || continuingTransactionId || !canContinueWithExistingDraftTransaction(transaction, currentUserId)) {
            return;
        }

        setContinuingTransactionId(transaction.id);
        try {
            const extendedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(store.wizard.account_id, transaction.id);
            const adoptedTrackingNumber = (extendedTransaction.tracking_number ?? trackingNumber).trim();

            await localUpdateTransaction(extendedTransaction);
            setTrackingNumberState(adoptedTrackingNumber);
            proceedToNextStep(adoptedTrackingNumber, extendedTransaction);
        } catch (error) {
            Logger.error('Failed to continue with existing transaction', error);
        } finally {
            setContinuingTransactionId(null);
        }
    };

    const onSubmit = async () => {
        const normalizedTrackingNumber = trackingNumber.trim();
        if (!store || isSubmitting || isGeneratingTrackingNumber || continuingTransactionId || normalizedTrackingNumber.length === 0) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (existingTransactions && existingTransactions.length > 0) return;

            const matches = await runTrackingNumberConflictCheck(normalizedTrackingNumber, store.data.transaction?.id);
            if (matches.length > 0) return;

            const transaction = await syncTransaction({
                tracking_number: normalizedTrackingNumber
            });

            proceedToNextStep(normalizedTrackingNumber, transaction);
        } catch (error) {
            Logger.error('Failed to submit tracking number', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        trackingNumber,
        setTrackingNumber,
        isSubmitting,
        isGeneratingTrackingNumber,
        existingTransactions,
        continuingTransactionId,
        currentUserId,
        submitDisabled:
            isSubmitting || isGeneratingTrackingNumber || !!continuingTransactionId || trackingNumber.trim().length === 0 || !!existingTransactions?.length,
        onSubmit,
        onGenerateTrackingNumber,
        onContinueWithExistingTransaction,
        onBack
    };
};

export default useEnterTrackingNumber;
